<template>
  <BaseInnerSidebar :show-title="false">
    <template #title>
      <div class="flex items-center gap-2">
        <i class="pi pi-briefcase text-primary-600" />
        <span>Business Profile</span>
      </div>
    </template>
    <template #content>
      <nav class="space-y-1">
        <nuxt-link
          v-for="(tab, index) in tabs"
          :key="index"
          :to="tab.route"
          class="block"
        >
          <BaseSidebarOption :is-active="tab.active">
            <Icon
              :name="tab.icon"
              class="text-base w-4"
            />
            <span>{{ tab.name }}</span>
          </BaseSidebarOption>
        </nuxt-link>
      </nav>

      <div class="px-4 py-4">
        <Divider />
        <div class="flex items-center gap-2 mb-3">
          <i class="pi pi-bolt text-amber-600 text-sm" />
          <h3 class="text-xs font-semibold text-gray-700 tracking-wide uppercase">
            Quick Links
          </h3>
        </div>
        <nav class="space-y-1">
          <div
            v-for="(item, index) in linkToItems"
            :key="index"
            @click="item.command()"
          >
            <BaseSidebarOption :is-active="item.active">
              <Icon
                :name="item.icon"
                class="text-base w-4"
              />
              <span>{{ item.name }}</span>
            </BaseSidebarOption>
          </div>
        </nav>
      </div>
    </template>
  </BaseInnerSidebar>

  <BusinessDialogBuilderCreate
    v-if="visibleBuilderCreateModal"
    v-model:visibleBuilderCreateModal="visibleBuilderCreateModal"
    v-model:activeBuilderType="activeBuilderType"
    v-model:activeBusiness="business"
    v-model:isLoading="isLoading"
    v-model:isThisAppraisalCreation="isThisAppraisalCreation"
  />

  <Dialog
    v-model:visible="contractDialog.visible"
    :header="business?.name"
    modal
    :style="{ width: '35rem' }"
  >
    <div class="flex items-center gap-4 mb-4">
      There are no contracts associated with this business. Would you like to create one now?
    </div>
    <div class="flex justify-end gap-2">
      <Button
        type="button"
        label="Cancel"
        severity="secondary"
        @click="contractDialog.visible = false"
      />
      <Button
        type="button"
        label="Confirm"
        @click="router.push({ name: 'contracts', query: { redirected: 'true', businessId: businessId } })"
      />
    </div>
  </Dialog>

  <!-- <Dialog
    v-model:visible="visibleBuilderCreateModal"
    header="Are you sure you want to proceed?"
    modal
    :style="{ width: '35rem' }"
  >
    <div class="flex items-center gap-4 mb-4 font-sans">
      This confirmation will only appear once
    </div>
    <div class="flex justify-end gap-2">
      <Button
        type="button"
        label="Cancel"
        severity="secondary"
        :loading="isGeneratingBuilder"
        @click="visibleBuilderCreateModal = false"
      />
      <Button
        type="button"
        label="Confirm"
        :loading="isGeneratingBuilder"
        @click="isThisAppraisalCreation ? generateAppraisal() : generateBuilder()"
      />
    </div>
  </Dialog> -->
</template>

<script lang="ts" setup>
import { useAuthStore } from '~/store/auth'
import { BusinessDetailIcon } from '~/types/baseIcon'

const route = useRoute()
const { t } = useI18n()

const router = useRouter()
const isLoading = ref(false)
const activeBuilderType = ref('')
const visibleBuilderCreateModal = ref(false)
const isGeneratingBuilder = ref(false)
const isThisAppraisalCreation = ref(false)
const contractDialog = ref({
  visible: false,
})

const businessId = computed(() => {
  return route.params?.businessId as string
})

const routeName = computed(() => {
  return route.name as string
})
const activeFilesRoutes = [
  'businesses-businessId-details-folders',
  'businesses-businessId-details-folders-folderId',
]
const activeMarketingRoutes = [
  'businesses-businessId-details-marketing',
]
const tabs = computed(() => {
  const items = [
    {
      name: 'Profile Manager',
      icon: BusinessDetailIcon.PROFILE,
      route: {
        name: 'businesses-businessId-details-profile',
        params: { businessId: businessId.value },
      },
      active: routeName.value === 'businesses-businessId-details-profile',
    },
    {
      name: 'File Room',
      icon: BusinessDetailIcon.FILES,
      route: {
        name: 'businesses-businessId-details-folders',
        params: { businessId: businessId.value },
      },
      active: activeFilesRoutes.includes(routeName.value),
    },
    {
      name: 'File Room Sharing',
      icon: BusinessDetailIcon.FILE_SHARING,
      route: {
        name: 'businesses-businessId-details-file-sharing',
        params: { businessId: businessId.value },
      },
      active: routeName.value === 'businesses-businessId-details-file-sharing',
    },
    {
      name: 'Activity Dashboard',
      icon: BusinessDetailIcon.DASHBOARD,
      route: {
        name: 'businesses-businessId-details-activity',
        params: { businessId: businessId.value },
      },
      active: routeName.value === 'businesses-businessId-details-activity',
    },
    {
      name: 'Advertisement Editor',
      icon: BusinessDetailIcon.ADVERTISEMENT,
      route: {
        name: 'businesses-businessId-details-teaser',
        params: { businessId: businessId.value },
      },
      active: routeName.value === 'businesses-businessId-details-teaser',
    },
    {
      name: 'Sharing Center',
      icon: BusinessDetailIcon.SHARING,
      route: {
        name: 'businesses-businessId-details-sharing',
        params: { businessId: businessId.value },
      },
      active: routeName.value === 'businesses-businessId-details-sharing',
    },
    {
      name: 'NDA Manager',
      icon: BusinessDetailIcon.EDITOR,
      route: {
        name: 'businesses-businessId-details-confidentiality-agreement',
        params: { businessId: businessId.value },
      },
      active: routeName.value === 'businesses-businessId-details-confidentiality-agreement',
    },
    {
      name: 'CIM Access Manager',
      icon: BusinessDetailIcon.CIM_ACCESS_MANAGER,
      route: {
        name: 'businesses-businessId-details-prospect-manager',
        params: { businessId: businessId.value },
      },
      active: routeName.value === 'businesses-businessId-details-prospect-manager',
    },

    {
      name: 'Email Marketing',
      icon: BusinessDetailIcon.MARKETING,
      route: {
        name: 'businesses-businessId-details-marketing',
        params: { businessId: businessId.value },
      },
      active: activeMarketingRoutes.includes(routeName.value),
    },

    // { name: 'Strategic Acquirer Finder', route: { name: 'businesses-businessId-details-acquisition-leads', params: { businessId: businessId.value } }, active: routeName.value === 'businesses-businessId-details-acquisition-leads' },

    // { name: 'CIM Access Manager', route: { name: 'businesses-businessId-details-cim-access-manager', params: { businessId: businessId.value } }, active: routeName.value === 'businesses-businessId-details-cim-access-manager' },
    {
      name: 'Buyer Analytics',
      icon: BusinessDetailIcon.ANALYTICS,
      route: {
        name: 'businesses-businessId-details-analytics',
        params: { businessId: businessId.value },
      },
      active: routeName.value === 'businesses-businessId-details-analytics',
    },

    // Automation Manager
    {
      name: 'Automation Manager',
      icon: BusinessDetailIcon.AUTOMATION_MANAGER,
      route: {
        name: 'businesses-businessId-details-automation-manager',
        params: { businessId: businessId.value },
      },
      active: routeName.value === 'businesses-businessId-details-automation-manager',
    },

    // { name: 'Video Recorder', route: { name: 'businesses-businessId-details-recorder', params: { businessId: businessId.value } }, active: routeName.value === 'businesses-businessId-details-recorder' },
    // Virtual Data Room (native VDR bolt-in)
    {
      name: 'Virtual Data Room',
      icon: BusinessDetailIcon.FILES,
      route: {
        name: 'businesses-businessId-details-vdr',
        params: { businessId: businessId.value },
      },
      active: routeName.value === 'businesses-businessId-details-vdr',
    },

  ]
  return items
})

const business = defineModel('business')
const builders = defineModel('builders')

const { userId, isAdmin, isOwner } = useRolePermissions()
const { user } = useUser()
const { user: activeUser } = storeToRefs(useAuthStore())
const isSupport = computed(() => {
  return user.value?.publicMetadata?.isAdmin || false
})

const checkIfICanAccessAListing = () => {
  console.log('🚀~business:', business.value)
  console.log('🚀~user:', user.value)
  if (isAdmin || isOwner || isSupport.value) {
    return true
  }

  return business.value?.BusinessUsers?.some((user: any) => user.UserWorkSpace?.userId === activeUser.value?.id)
}

const toast = useToast()
const linkToItems = computed(() => {
  return [
    {
      name: 'Link to Appraisal', icon: BusinessDetailIcon.APPRAISAL, type: 'appraisal', active: false, command: () => {
        console.log('DATA:', builders.value)
        if (builders.value.find(el => el.builderType == 'APPRAISAL')) {
          router.push({ name: 'businesses-businessId-appraisal', params: { businessId: businessId.value } })
        }
        else {
          if (!checkIfICanAccessAListing()) {
            toast.add({
              severity: 'error',
              summary: 'Error',
              detail: 'You do not have access to this listing',
              life: 3000,
            })
            return
          }
          activeBuilderType.value = 'APPRAISAL'
          isThisAppraisalCreation.value = true
          visibleBuilderCreateModal.value = true
        }
      },
    },
    {
      name: 'Link to Proposal', icon: BusinessDetailIcon.PROPOSAL, type: 'proposal', active: false, command: () => {
        console.log('DATA:', builders.value)

        if (builders.value.find(el => el.builderType == 'PROPOSAL')) {
          router.push({ name: 'businesses-businessId-proposal', params: { businessId: businessId.value } })
        }
        else {
          if (!checkIfICanAccessAListing()) {
            toast.add({
              severity: 'error',
              summary: 'Error',
              detail: 'You do not have access to this listing',
              life: 3000,
            })
            return
          }
          activeBuilderType.value = 'PROPOSAL'
          isThisAppraisalCreation.value = false
          visibleBuilderCreateModal.value = true
        }
      },
    },
    {
      name: 'Link to CIM', icon: BusinessDetailIcon.CIM, type: 'cim', active: false, command: () => {
        if (builders.value.find(el => el.builderType == 'CIM')) {
          router.push({ name: 'businesses-businessId-cim', params: { businessId: businessId.value } })
        }
        else {
          if (!checkIfICanAccessAListing()) {
            toast.add({
              severity: 'error',
              summary: 'Error',
              detail: 'You do not have access to this listing',
              life: 3000,
            })
            return
          }
          activeBuilderType.value = 'CIM'
          isThisAppraisalCreation.value = false
          visibleBuilderCreateModal.value = true
        }
      },

    },

    {
      name: 'Link to Teaser', icon: BusinessDetailIcon.TEASER, type: 'teaser', active: false, command: () => {
        if (builders.value.find(el => el.builderType == 'TEASER')) {
          router.push({ name: 'businesses-businessId-teaser', params: { businessId: businessId.value } })
        }
        else {
          if (!checkIfICanAccessAListing()) {
            toast.add({
              severity: 'error',
              summary: 'Error',
              detail: 'You do not have access to this listing',
              life: 3000,
            })
            return
          }
          activeBuilderType.value = 'TEASER'
          isThisAppraisalCreation.value = false
          visibleBuilderCreateModal.value = true
        }
      },
    },
    {
      name: 'Link to Contracts', icon: BusinessDetailIcon.CONTRACT, type: 'contract', active: false, command: () => {
        if (business.value?.Contract?.length) {
          router.push({ name: 'contracts', query: { businessId: business.value.id } })
        }
        else {
          contractDialog.value.visible = true
        }
      },

    },
    {
      name: 'Video Recorder', icon: BusinessDetailIcon.VIDEO_RECORDER, type: 'video-recorder', active: routeName.value === 'businesses-businessId-details-recorder', command: () => {
        router.push({ name: 'businesses-businessId-details-recorder', params: { businessId: businessId.value } })
      },
    },
  ]
})

// Watch for changes in businessId and reload data

// const business = ref()

// const { getBusinessById } = useBusiness()
// onMounted(async () => {
//   await loadBusiness()
// })

// create builder
const { createBuilder } = useBuilder()
const generateBuilder = async () => {
  isGeneratingBuilder.value = true
  await createBuilder({
    title: business?.value?.name,
    businessId: business?.value?.id,
    workSpaceId: business?.value?.workSpaceId,
    builderType: activeBuilderType.value,
  })

  if (activeBuilderType.value === 'CIM') {
    // if this is creating a CIM builder then we also create a CA builder.
    await createBuilder({
      title: business?.value?.name,
      businessId: business?.value?.id,
      workSpaceId: business?.value?.workSpaceId,
      builderType: 'CA',
    })
  }
  router.push(`/businesses/${business?.value?.id}/${activeBuilderType.value.toLowerCase()}`)
  isGeneratingBuilder.value = false
  visibleBuilderCreateModal.value = false
}

const { saveAppraisal } = useAppraisal()
const generateAppraisal = async () => {
  isGeneratingBuilder.value = true
  await saveAppraisal(business.value.id, {})
  isGeneratingBuilder.value = false
  visibleBuilderCreateModal.value = false
  router.push(`/businesses/${business?.value?.id}/appraisal`)
}
</script>
